import { FC, useState, useRef, useEffect } from 'react';
import { Skeleton } from '@repo/atomic-components';
import { BasePdfSinglePageRenderer } from '@repo/atomic-components/pdf';

interface LazyPdfRendererProps {
  pdfFile: string | File | null;
  height: number;
  width: number;
  pageInputRef?: React.Ref<HTMLDivElement | null>;
  className?: string;
}

const LazyPdfRenderer: FC<LazyPdfRendererProps> = ({ pdfFile, height, width, pageInputRef, className }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (hasBeenVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '300px 0px', // Load 300px before entering viewport
        threshold: 0.1, // Between
      },
    );

    if (ref.current) {
      observer.observe(ref.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasBeenVisible]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        height,
        width,
        minHeight: height,
        minWidth: width,
        position: 'relative',
      }}
    >
      {isVisible ? (
        // mainSide="width" so react-pdf scales the canvas to match our container width
        // (which is set from the page's true aspect ratio). Bundle viewer uses the same
        // approach (BundlePage/components/PdfPageContainer.tsx).
        <BasePdfSinglePageRenderer
          pdfFile={pdfFile}
          height={height}
          width={width}
          mainSide="width"
          pageInputRef={pageInputRef}
        />
      ) : (
        <Skeleton height={`${height}px`} width={`${width}px`} className="rounded-sm" />
      )}
    </div>
  );
};

export { LazyPdfRenderer };
